import { useCallback } from "react";
import { useI18n } from "@wato/i18n/react";
import { formatMoney, isCurrencyCode, money } from "@wato/money";

/** Minor-unit amount as the API sends it (decimal string, number or bigint). */
type MinorAmount = string | number | bigint;

/**
 * Formats integer minor units with `@wato/money` (currency-aware: SAR 2 dp, BHD 3 dp; AR/EN
 * symbol placement). An unknown/missing currency or an unparsable amount renders `—` instead of a
 * guessed `/ 100` value.
 */
export function formatMinor(
  minor: MinorAmount | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string {
  if (minor === null || minor === undefined || !currency || !isCurrencyCode(currency)) return "—";
  try {
    return formatMoney(money(minor, currency), locale === "ar" ? "ar" : "en");
  } catch {
    return "—";
  }
}

/** `formatMinor` bound to the app locale. */
export function useFormatMinor(): (
  minor: MinorAmount | null | undefined,
  currency: string | null | undefined,
) => string {
  const { locale } = useI18n();
  return useCallback((minor, currency) => formatMinor(minor, currency, locale), [locale]);
}

/** Home currency of a workspace (KSA → SAR, Bahrain → BHD). */
export function workspaceCurrency(country: string | null | undefined): "SAR" | "BHD" {
  return country === "BH" ? "BHD" : "SAR";
}
